/**
 * Utility functions for form response formatting and manipulation
 */

/**
 * Build a display name from form response data.
 * Prefers firstName + lastName; falls back to legacy 'name' field.
 * @param {Object} responses - Response field mapping (e.g., { firstName, lastName, name })
 * @returns {string} - Display name or empty string
 */
const buildDisplayName = (responses) => {
    if (!responses) return '';
    const first = (responses.firstName || '').trim();
    const last = (responses.lastName || '').trim();
    if (first || last) return `${first} ${last}`.trim();
    return (responses.name || '').trim();
};

/**
 * Format response data for display in table
 * @param {FormResponse} response - Raw response from Firebase
 * @param {Object} options - Formatting options
 * @param {Object} [options.propertiesMap] - Map of propertyId → display name
 * @param {string} [options.portfolioName] - Portfolio display name
 * @returns {Object} - Formatted response
 */
export const formatResponseForTable = (response, options = {}) => {
    const { fieldsConfig = {}, propertiesMap = {}, portfolioName = '' } = options;

    return {
        id: response.id,
        name: buildDisplayName(response.responses) || 'Unknown',
        email: response.responses?.email || '',
        phone: response.responses?.phone || '',
        source: formatResponseSource(response, { propertiesMap, portfolioName }),
        submittedAt: response.createdAt,
        submittedAtFormatted: formatSubmissionTime(response.createdAt),
        type: response.type,
    };
};

/**
 * Format response source based on type and context
 * Resolves actual property/portfolio names when context is available
 * @param {FormResponse} response
 * @param {Object} context - Lookup context
 * @param {Object} [context.propertiesMap] - Map of propertyId → display name
 * @param {string} [context.portfolioName] - Portfolio display name
 * @returns {string}
 */
export const formatResponseSource = (response, context = {}) => {
    const { propertiesMap = {}, portfolioName = '' } = context;

    switch (response.type) {
        case 'portfolio':
            return portfolioName || 'Main Website';
        case 'property': {
            if (!response.propertyId) return 'Unknown';
            const name = propertiesMap[response.propertyId];
            return name || 'Untitled Property';
        }
        default:
            return 'Form Submission';
    }
};

/**
 * Format submission time for display as absolute date (DD MMM YYYY)
 * @param {Date|string|null} timestamp
 * @returns {string}
 */
export const formatSubmissionTime = (timestamp) => {
    if (!timestamp) return 'Unknown';

    try {
        const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
        return formatDateDDMMMYYYY(date);
    } catch (error) {
        console.warn('[formatSubmissionTime] Error formatting date:', error);
        return 'Unknown';
    }
};

/**
 * Format date as DD MMM YYYY (e.g., 26 Jan 2026)
 * @param {Date} date
 * @returns {string}
 */
export const formatDateDDMMMYYYY = (date) => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
        return 'Unknown';
    }
    const day = date.getDate().toString().padStart(2, '0');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
};

/**
 * Group form fields by section for detail view
 * @param {Object} responses - Field ID to value mapping
 * @param {Array<FormField>} fieldSchema - Optional field schema
 * @returns {Object} - { [sectionName]: [{ key, label, value, type, ... }] }
 */
export const groupResponseFieldsBySection = (responses, fieldSchema = []) => {
    // Guard against null or undefined responses
    if (!responses || typeof responses !== 'object') {
        return {
            'Contact Information': [],
            'Additional Information': [],
            'Metadata': [],
        };
    }

    const grouped = {
        'Contact Information': [],
        'Additional Information': [],
        'Metadata': [],
    };

    // If schema provided, use it for ordering and grouping
    if (fieldSchema && fieldSchema.length > 0) {
        fieldSchema.forEach((field) => {
            const section = field.section || 'Additional Information';
            if (!grouped[section]) grouped[section] = [];

            let fieldValue = responses[field.id];
            // Resolve multiselect IDs to display names if a Display companion exists
            if (Array.isArray(fieldValue) && responses[`${field.id}Display`]) {
                fieldValue = responses[`${field.id}Display`];
            }

            grouped[section].push({
                id: field.id,
                label: field.label || field.id,
                value: fieldValue,
                type: field.type,
                section,
            });
        });
    }

    // Fallback: categorize by field names
    const contactFields = ['name', 'email', 'phone', 'address'];
    const metaFields = ['pageUrl', 'referrer', 'userAgent', 'sourceIP'];
    // Skip auto-generated display companion fields (e.g. interestedPropertyDisplay)
    const displaySuffixKeys = new Set(Object.keys(responses).filter(k => k.endsWith('Display')));

    Object.entries(responses).forEach(([key, value]) => {
        // Skip if already added from schema
        if (fieldSchema && fieldSchema.length > 0 && fieldSchema.some(f => f.id === key)) return;
        // Skip display companion fields — they are resolved inline below
        if (displaySuffixKeys.has(key)) return;

        // Resolve multiselect IDs to display names if a Display companion exists
        let displayValue = value;
        if (Array.isArray(value) && responses[`${key}Display`]) {
            displayValue = responses[`${key}Display`];
        }

        let targetSection = 'Additional Information';
        if (contactFields.includes(key.toLowerCase())) {
            targetSection = 'Contact Information';
        } else if (metaFields.includes(key)) {
            targetSection = 'Metadata';
        }

        grouped[targetSection].push({
            id: key,
            label: formatFieldLabel(key),
            value: displayValue,
            type: Array.isArray(value) ? 'multiselect' : 'text',
            section: targetSection,
        });
    });

    // Remove empty sections
    return Object.entries(grouped).reduce((acc, [section, fields]) => {
        if (fields.length > 0) acc[section] = fields;
        return acc;
    }, {});
};

/**
 * Format field ID/key to human-readable label
 * @param {string} fieldId
 * @returns {string}
 */
export const formatFieldLabel = (fieldId) => {
    return fieldId
        .replace(/([A-Z])/g, ' $1')
        .replace(/_/g, ' ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ')
        .trim();
};

/**
 * Extract critical contact fields from response
 * @param {FormResponse} response
 * @returns {Object} - { name, firstName, lastName, email, phone }
 */
export const extractContactInfo = (response) => {
    const firstName = (response.responses?.firstName || '').trim();
    const lastName = (response.responses?.lastName || '').trim();
    const legacyName = (response.responses?.name || '').trim();
    const displayName = (firstName || lastName) ? `${firstName} ${lastName}`.trim() : legacyName;
    return {
        name: displayName,
        firstName,
        lastName,
        email: response.responses?.email || '',
        phone: response.responses?.phone || '',
    };
};

/**
 * Extract metadata from response
 * @param {FormResponse} response
 * @returns {Object} - { pageUrl, referrer, userAgent, sourceIP }
 */
export const extractMetadata = (response) => {
    return {
        pageUrl: response.meta?.pageUrl || '',
        referrer: response.meta?.referrer || '',
        userAgent: response.meta?.userAgent || '',
        sourceIP: response.meta?.sourceIP || '',
        submittedAt: response.createdAt,
    };
};

/**
 * Render field value based on type
 * @param {string} type - Field type
 * @param {any} value - Field value
 * @returns {React.ReactNode}
 */
export const renderFieldValue = (type, value) => {
    if (value === null || value === undefined || value === '') {
        return <span className="text-stone-400 italic">—</span>;
    }

    switch (type) {
        case 'checkbox':
            return value ? '✓ Yes' : '✗ No';
        case 'date':
            try {
                const date = new Date(value);
                return date.toLocaleDateString();
            } catch {
                return String(value);
            }
        case 'email':
            return <a href={`mailto:${value}`} className="text-blue-600 hover:underline">{value}</a>;
        case 'phone':
            return <a href={`tel:${value}`} className="text-blue-600 hover:underline">{value}</a>;
        case 'textarea':
            return <div className="whitespace-pre-wrap break-words">{value}</div>;
        case 'file':
            return <span className="text-stone-600">[File: {value}]</span>;
        case 'multiselect':
            if (Array.isArray(value)) {
                return value.join(', ');
            }
            return String(value);
        default:
            if (Array.isArray(value)) {
                return value.join(', ');
            }
            return String(value);
    }
};

/**
 * Check if response is recent (submitted within last 24 hours)
 * @param {FormResponse} response
 * @returns {boolean}
 */
export const isResponseRecent = (response) => {
    if (!response.createdAt) return false;

    const date = response.createdAt?.toDate ? response.createdAt.toDate() : new Date(response.createdAt);
    const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
    return date > oneDayAgo;
};
